import { IAuthenticator } from "@/domain/repositories/Auth";
import { ITokenDecoder } from "@/domain/repositories/DecodeToken";
import { INetworkClient } from "@/domain/repositories/NetworkClient";
import { IRouteAuthLogin } from "@/types/src/backend/routes/auth/login";
import { IRouteAuthRefreshToken } from "@/types/src/backend/routes/auth/refresh-token";
import { ContentToken } from "@/types/src/backend/utils/jwt";

export class AuthenticatorUsecase {
  constructor(
    private readonly authenticator: IAuthenticator,
    private readonly networkClient: INetworkClient,
    private readonly decoder: ITokenDecoder
  ) {}

  async providerLogin() {
    let authData;
    try {
      authData = await this.authenticator.authenticate();
    } catch (e) {
      throw new Error(`Authentication failed: ${e}`);
    }

    return {
      type: this.authenticator.type,
      ...authData,
    };
  }

  async login(
    loginCredentials: IRouteAuthLogin["body"]
  ): Promise<IRouteAuthLogin["outputData"]> {
    const mdcResponse = await this.networkClient.post(
      "/auth/login",
      {
        ...loginCredentials,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!mdcResponse || mdcResponse.error) {
      throw new Error(`Authentication failed: ${mdcResponse.error}`);
    }

    return mdcResponse;
  }

  async logout() {
    await this.authenticator.deauthenticate();
  }

  async silentLogin(
    token: string,
    refreshToken?: string
  ): Promise<string | null> {
    const isTokenValid = await this.isTokenValid(token);

    if (isTokenValid) {
      try {
        await this.decodeToken(token);
        return token;
      } catch (e) {
        if (process.env.NODE_ENV === "development") return null;
      }
    }

    if (!refreshToken) {
      return null;
    }

    // Try to refresh token
    try {
      const newToken = await this.refreshToken(refreshToken);
      await this.decodeToken(newToken);
      return newToken;
    } catch (e) {
      return null;
    }
  }

  async decodeToken(token: string): Promise<ContentToken> {
    const user = await this.decoder.decode(token);

    if (!user || !user.userId) {
      throw new Error("Invalid token");
    }

    return user;
  }

  private async isTokenValid(token: string) {
    if (!token) {
      return false;
    }

    const user = await this.decoder.decode(token);

    if (user.exp * 1000 < Date.now()) {
      return false;
    }
    return true;
  }

  private async refreshToken(
    refreshToken: string
  ): Promise<IRouteAuthRefreshToken["outputData"]["token"]> {
    const isTokenValid = await this.isTokenValid(refreshToken);

    if (!isTokenValid) {
      throw new Error("Refresh token expired. Please login again.");
    }

    const mdcResponse = await this.networkClient.post(
      "/auth/refresh-token",
      {
        refreshToken,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (mdcResponse.error) {
      throw new Error(`Authentication failed: ${mdcResponse.error}`);
    }

    return mdcResponse.data;
  }
}
